/**
 * MechaGuard - SkillSystem (能量与特殊技能)
 *
 * 玩家除建造防御塔外，还能消耗「能量」释放主动技能。
 * 本模块只负责**资源与冷却的账本**：能量收支、冷却计时、可否释放。
 * 技能的实际效果由 Game._applySkillEffect() 实现 —— 这样 SkillSystem
 * 不需要知道敌人、特效、地图的存在，便于单独测试。
 *
 * 能量来源：
 *   - 开局 1 点
 *   - 每清空一波 +1
 *   - 每击杀一个 BOSS +2
 */

/** 能量上限 */
export const MAX_ENERGY = 5;
/** 开局能量 */
export const START_ENERGY = 1;
/** 每清空一波获得 */
export const ENERGY_PER_WAVE = 1;
/** 每击杀一个 BOSS 获得 */
export const ENERGY_PER_BOSS = 2;

/** 技能定义（顺序即 UI 显示顺序） */
export const SKILLS = Object.freeze([
  {
    id: 'emp',
    name: 'EMP 冲击波',
    icon: '💥',
    cost: 1,
    cooldown: 20,
    /** 'instant' 立即生效；'point' 需要在地图上指定位置 */
    targeting: 'instant',
    color: '#00ffff',
    desc: '冻结全场敌人 1.5 秒',
  },
  {
    id: 'shield',
    name: '应急护盾',
    icon: '🛡️',
    cost: 2,
    cooldown: 45,
    targeting: 'instant',
    color: '#00ff88',
    desc: '获得 5 点护盾，漏怪时优先消耗护盾',
  },
  {
    id: 'orbital',
    name: '轨道打击',
    icon: '☄️',
    cost: 3,
    cooldown: 60,
    targeting: 'point',
    color: '#ff9900',
    desc: '指定位置造成 500 点范围伤害',
  },
]);

export class SkillSystem {
  /**
   * @param {object} [opts]
   * @param {()=>void} [opts.onEnergyChange]
   * @param {()=>void} [opts.onCooldownChange]
   */
  constructor({ onEnergyChange = null, onCooldownChange = null } = {}) {
    this.onEnergyChange = onEnergyChange;
    this.onCooldownChange = onCooldownChange;

    this.energy = START_ENERGY;
    this.maxEnergy = MAX_ENERGY;
    /** @type {Map<string, number>} id -> 剩余冷却秒数 */
    this.cooldowns = new Map();

    this.reset();
  }

  /* ===================================================
   * 生命周期
   * =================================================== */

  reset() {
    this.energy = START_ENERGY;
    this.cooldowns.clear();
    for (const skill of SKILLS) this.cooldowns.set(skill.id, 0);

    this._emitEnergy();
    this._emitCooldowns();
  }

  /** @param {number} dt 秒 */
  update(dt) {
    let cooling = false;

    for (const skill of SKILLS) {
      const cd = this.cooldowns.get(skill.id) || 0;
      if (cd > 0) {
        this.cooldowns.set(skill.id, Math.max(0, cd - dt));
        cooling = true;
      }
    }

    // 只在还有技能在冷却时才通知 UI（UI 内部再按整数秒去重）
    if (cooling) this._emitCooldowns();
  }

  /* ===================================================
   * 查询
   * =================================================== */

  /** @param {string} id */
  getSkill(id) {
    return SKILLS.find(s => s.id === id) || null;
  }

  /** 剩余冷却秒数 */
  cooldownOf(id) {
    return this.cooldowns.get(id) || 0;
  }

  /** 冷却进度：1 表示已就绪 */
  cooldownRatio(id) {
    const skill = this.getSkill(id);
    if (!skill || skill.cooldown <= 0) return 1;

    const cd = this.cooldownOf(id);
    return cd <= 0 ? 1 : 1 - cd / skill.cooldown;
  }

  /** 能量是否够（不看冷却） */
  hasEnergyFor(id) {
    const skill = this.getSkill(id);
    return !!skill && this.energy >= skill.cost;
  }

  /**
   * 能否释放
   * @returns {{ok:boolean, reason:string}}
   */
  canCast(id) {
    const skill = this.getSkill(id);
    if (!skill) return { ok: false, reason: '未知技能' };

    const cd = this.cooldownOf(id);
    if (cd > 0) {
      return { ok: false, reason: `${skill.name} 冷却中（${Math.ceil(cd)} 秒）` };
    }

    if (this.energy < skill.cost) {
      return { ok: false, reason: `能量不足（需要 ${skill.cost} 点）` };
    }

    return { ok: true, reason: '' };
  }

  /* ===================================================
   * 变更
   * =================================================== */

  /**
   * 扣除能量并进入冷却。
   * 必须由调用方在**确认技能效果已生效后**才调用，否则会出现
   * 「什么都没打到却扣了能量」的挫败感。
   */
  consume(id) {
    const skill = this.getSkill(id);
    if (!skill) return false;

    this.energy = Math.max(0, this.energy - skill.cost);
    this.cooldowns.set(id, skill.cooldown);

    this._emitEnergy();
    this._emitCooldowns();
    return true;
  }

  /** 增加能量（自动截断到上限） */
  addEnergy(amount) {
    if (!(amount > 0)) return;

    const before = this.energy;
    this.energy = Math.min(this.maxEnergy, this.energy + amount);

    if (this.energy !== before) this._emitEnergy();
  }

  /* ===================================================
   * 内部
   * =================================================== */

  _emitEnergy() {
    if (this.onEnergyChange) this.onEnergyChange();
  }

  _emitCooldowns() {
    if (this.onCooldownChange) this.onCooldownChange();
  }
}
